import { Component, Inject, Input, OnInit, signal } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Store } from '@ngneat/elf';
import { withUIEntities } from '@ngneat/elf-entities';
import { SelectItem } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { unitsLib } from 'src/app/core/services/unit-library';
import { DlsOverridesService } from 'src/app/perivis/services/dls-override.service';
import { DlsOveridesUi, DlsOverrideModel } from 'src/app/shared/models/dlsOverride.model';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { TotalDepthResult, TrajectoryModel } from 'src/app/shared/models/trajectory.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import {
  createDlsOverrideColumns,
  createDlsOverrideRow,
  ensureTrailingInputRow,
  extractDlsOverrideRows,
  populateDlsOverrideFormArray
} from './helpers/dls-overrides.helper';

@Component({
    selector: 'app-dls-overrides',
    templateUrl: './dls-overrides.component.html',
    standalone: false
})
export class DlsOverridesComponent implements OnInit {
  private totalDepth: TotalDepthResult;

  public isLoading = signal<boolean>(false);
  public userUnits = signal<UserUnitsModel | null>(null);
  public dlsOverrides: DlsOverrideModel[];
  public readonly dlsOverrideColumns = createDlsOverrideColumns();
  public dlsOverrideRows = new FormArray<FormGroup>([]);
  public createDlsOverrideRow = createDlsOverrideRow;

  public tablePlot: SelectItem[] = [
    { label: 'Table', value: 'table' },
    { label: 'Plot', value: 'plot' }
  ];
  public trajectories: TrajectoryModel[];
  public yAxisTitle = '';
  public xAxisTitle = '';
  public plotName = 'additionalDlsPlot';
  public downloadPlotName = 'additional_dls_plot';
  public plotSeries = signal<any[]>([]);

  //State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.dlsOverridesStore = this._storeService.createStore(this.componentId, new DlsOveridesUi, withUIEntities<XyLinePlotUi>());
    this.tablePlotSelected.set(this.dlsOverridesStore.state.tablePlotSelected ?? 'table');
  }
  get componentId(): string {
    return this._componentId;
  }
  public dlsOverridesStore: Store;
  public tablePlotSelected = signal<string>('table');

  constructor(
    @Inject(MediatorService) private _messenger: MediatorService,
    @Inject(DlsOverridesService) private _dlsOverrideService: DlsOverridesService,
    @Inject(TrajectoryService) private _trajectoryService: TrajectoryService,
    @Inject(StoreService) private _storeService: StoreService
  ) {}

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    const [dlsOverrides, trajectories, totalDepth, uu] = await Promise.all([
      lastValueFrom(this._dlsOverrideService.getDlsOverrides()),
      lastValueFrom(this._trajectoryService.getTrajectoryPoints()),
      lastValueFrom(this._trajectoryService.getTotalDepth()),
      this._storeService.get<UserUnitsModel>(StorageKeys.UNITS)
    ]);
    this.userUnits.set(uu);
    this.dlsOverrides = dlsOverrides || [];
    this.trajectories = trajectories;
    this.totalDepth = totalDepth;

    populateDlsOverrideFormArray(this.dlsOverrideRows, this.dlsOverrides);

    const dlsUnit = unitsLib[uu.doglegSeverity].symbol;
    this.yAxisTitle = `MD (${uu.longLengths})`;
    this.xAxisTitle = `Dogleg (${dlsUnit})`;
    await this.plotData();

    this.isLoading.set(false);
  }

  public async tablePlotToggle(e) {
    const selectedView = e?.value ?? 'table';
    this.tablePlotSelected.set(selectedView);
    this.dlsOverridesStore.update(state => ({ ...state, tablePlotSelected: selectedView }));
    await this.plotData();
  }

  async plotData() {
    if (this.tablePlotSelected() === 'table') {
      return;
    }
    const overrides = this.dlsOverrides ?? [];
    const trajectory = await lastValueFrom(this._trajectoryService.getTrajectoryPoints());
    trajectory.forEach((t) => {
      const dlsOverride = overrides.find((d) => d.measuredDepth >= t.measuredDepth)?.dlsOverride;
      if (dlsOverride) {
        t.doglegSeverity += dlsOverride;
      }

      overrides.forEach((d) => {
        const depth = d.measuredDepth;
        if (trajectory.findIndex((x) => x.measuredDepth == depth) == -1) {
          const dls = d.dlsOverride;
          trajectory.push({ measuredDepth: depth, doglegSeverity: dls, inclination: 0, azimuth: 0, trueVerticalDepth: 0, northing: 0, easting: 0 });
        }
      });
    });

    const additPoints = {
      x: overrides.map(x => x.dlsOverride),
      y: overrides.map(x => x.measuredDepth),
      name: 'Additional Dogleg',
      mode: 'lines',
      line: {
        color: 'red',
        dash: 'dot',
        width: 4
      },
    };

    if (overrides.length > 0) {
      // Insert point above the first override so the step line starts at surface.
      additPoints.x.splice(0, 0, overrides[0].dlsOverride);
      additPoints.y.splice(0, 0, 0);

      // Add point at total depth so the final step reaches the bottom of the well.
      additPoints.x.push(0);
      additPoints.y.push(this.totalDepth.measuredDepth);
    }

    // Sort trajectory
    trajectory.sort((a, b) => a.measuredDepth - b.measuredDepth);

    const dogleg = {
      x: trajectory.map(x => x.doglegSeverity),
      y: trajectory.map(x => x.measuredDepth),
      name: 'Total Dogleg',
      mode: 'lines'
    };

    for (let x = additPoints.x.length - 1; x > 0; x--) {
      additPoints.x.splice(x, 0, additPoints.x[x]);
      additPoints.y.splice(x, 0, additPoints.y[x - 1]);
    }

    for (let i = dogleg.x.length - 1; i > 0; i--) {
      dogleg.x.splice(i, 0, dogleg.x[i]);
      dogleg.y.splice(i, 0, dogleg.y[i - 1]);
    }

    this.plotSeries.set(additPoints.x.length > 0
      ? [dogleg, additPoints]
      : [dogleg]);
  }

  public onDlsOverrideRowDeleted(index: number): void {
    if (index >= 0 && index < this.dlsOverrideRows.length) {
      this.dlsOverrideRows.removeAt(index);
    }
    this.onDlsOverrideGridChange();
  }

  public onDlsOverrideRowsReset(): void {
    populateDlsOverrideFormArray(this.dlsOverrideRows, [
      { measuredDepth: this.totalDepth?.measuredDepth ?? 0, dlsOverride: 0 }
    ]);
    this.onDlsOverrideGridChange();
  }

  public onDlsOverrideGridChange(): void {
    ensureTrailingInputRow(this.dlsOverrideRows);
    const valueToSave = extractDlsOverrideRows(this.dlsOverrideRows, this.totalDepth?.measuredDepth);
    this.dlsOverrides = valueToSave;
    this.plotData();

    this._dlsOverrideService.updateDlsOverrides(valueToSave).subscribe(() => {
      this._messenger.publish(new DataSavedMessage('Additional Doglegs'));
    });
  }
}
